import {
  AutocompleteArrayInput,
  AutocompleteInput,
  ArrayInput,
  BooleanInput,
  EditButton,
  FormDataConsumer,
  ReferenceArrayInput,
  ReferenceInput,
  regex,
  AddItemButton,
  required,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  useSimpleFormIterator,
  useTranslate,
} from 'react-admin';
import {
  List, Datagrid, IfCanAccess,
} from '@react-admin/ra-rbac';
import * as React from 'react';
import { Typography } from '@mui/material';
import { get } from 'lodash';
import { CustomToolbar } from '../../../components/CustomToolBar';
import SearchFields, { FilterResetter } from '../components/fields/Filters';
import ActiveBooleanField from '../../../components/fields/ActiveBooleanField';
import SimpleChipListField from '../../../components/fields/SimpleChipListField';
import CustomBulkActionButtons from '../../../components/buttons/CustomBulkActionButtons';
import { exporter } from '../../../utils/customExporter';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { LIST_DEBOUNCE } from '../../../config';
import { StabilityAlert, StabilityLevel } from '../../../components/StabilityAlert';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';
import { HasRoles } from '../../../components/HasRoles';
import { AuditlogButton } from '../components/buttons/AuditlogButton';
import {
  alarmSeverities, alarmTypes, appModeOptions, daysOfWeek,
} from '../../../lib/constants/selectChoices';
import { localeDefaults } from '../../../lib/constants/localesOptions';

const observationTriggerEmailLocales = [
  ...localeDefaults,
  { name: 'no', id: 'no' },
];

const eventCodes: Array<{ name: string; id: string }> = [
  { name: 'ALARM_GREEN', id: 'ALARM_GREEN' },
  { name: 'ALARM_ORANGE', id: 'ALARM_ORANGE' },
  { name: 'ALARM_RED', id: 'ALARM_RED' },
  { name: 'MANDOWN', id: 'MANDOWN' },
];

const actionTypes = [
  { id: 'alarm', name: 'resources.observation-triggers.action_types.alarm' },
  { id: 'sms', name: 'resources.observation-triggers.action_types.sms' },
  { id: 'email', name: 'resources.observation-triggers.action_types.email' },
  { id: 'assetUpdate', name: 'resources.observation-triggers.action_types.assetUpdate' },
];

const conditionTypes = [
  { id: 'operator/and', name: 'resources.observation-triggers.filter_types.operator_and' },
  { id: 'operator/or', name: 'resources.observation-triggers.filter_types.operator_or' },
  { id: 'constraint/asset', name: 'resources.observation-triggers.filter_types.constraint_asset' },
  { id: 'constraint/assetGroup', name: 'resources.observation-triggers.filter_types.constraint_assetGroup' },
  { id: 'constraint/zone', name: 'resources.observation-triggers.filter_types.constraint_zone' },
  { id: 'constraint/schedule', name: 'resources.observation-triggers.filter_types.constraint_schedule' },
  { id: 'operator/not', name: 'resources.observation-triggers.filter_types.operator_not' },
];

const conditionBlockTypes = [
  { id: 'operator/and', name: 'resources.observation-triggers.filter_types.operator_and' },
  { id: 'operator/not', name: 'resources.observation-triggers.filter_types.operator_not' },
];

const matchTypes = [
  { id: 'matches', name: 'resources.observation-triggers.match_types.matches' },
  { id: 'doesNotMatch', name: 'resources.observation-triggers.match_types.does_not_match' },
];

const targetConditionTypeIds = ['constraint/asset', 'constraint/assetGroup', 'constraint/zone'];

const targetConditionTypes = conditionTypes.filter((conditionType) => (
  targetConditionTypeIds.includes(conditionType.id)
));

const availabilityChoices = [
  { id: true, name: 'resources.observation-triggers.fields.config.available_true' },
  { id: false, name: 'resources.observation-triggers.fields.config.available_false' },
];

const timeValidator = [required(), regex(/^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/, 'resources.observation-triggers.validation.time')];

const sourcePath = (source: string, field: string) => (source ? `${source}.${field}` : field);

const PrimitiveArrayTextInput = ({ source, label }: { source: string; label: string }) => (
  <ArrayInput source={source} label={label}>
    <SimpleFormIterator disableReordering>
      <TextInput source="" label={label} validate={required()}/>
    </SimpleFormIterator>
  </ArrayInput>
);

const defaultFilterCondition = { match: 'matches', type: 'constraint/asset' };

const AddIteratorButton = ({ translationKey, defaultItem }: { translationKey: string; defaultItem?: any }) => {

  const translate = useTranslate();
  const { add } = useSimpleFormIterator();
  return (
    <div>
      {defaultItem ? <AddItemButton onClick={() => add(defaultItem)}/> : <AddItemButton/>}
      <Typography variant="body1" sx={{ display: 'inline', fontSize: '15px' }}>
        {translate(translationKey)}
      </Typography>
    </div>
  );

};

const getEventCodeChoices = (values?: string[]) => {

  const customEventCodes = (values || [])
    .filter((value) => value && !eventCodes.some((eventCode) => eventCode.id === value))
    .map((value) => ({ id: value, name: value }));
  return [
    ...eventCodes,
    ...customEventCodes,
  ];

};

const EventCodesInput = () => (
  <ArrayInput source="eventCodes" validate={required()}>
    <SimpleFormIterator disableReordering
      addButton={<AddIteratorButton translationKey="resources.observation-triggers.text.add_eventcode"/>}>
      <FormDataConsumer>
        {({ formData }) => (
          <AutocompleteInput
            source=""
            onCreate={(value) => {

              if (value) {

                const newEventCode = { id: value, name: value };
                eventCodes.push(newEventCode);
                return newEventCode;

              }
              return null;

            }}
            choices={getEventCodeChoices(formData?.eventCodes)}
            freeSolo
            label={false}
            sx={{
              '& .MuiOutlinedInput-notchedOutline legend': {
                display: 'none',
              },
            }}
            validate={required()}
          />
        )}
      </FormDataConsumer>
    </SimpleFormIterator>
  </ArrayInput>
);

const ContactTargetsInput = () => (
  <>
    <PrimitiveArrayTextInput source="config.toRaw" label="resources.observation-triggers.fields.config.toRaw"/>
    <ReferenceArrayInput source="config.toContactList" reference="contact-lists">
      <AutocompleteArrayInput label="resources.observation-triggers.fields.config.toContactList" fullWidth/>
    </ReferenceArrayInput>
    <BooleanInput source="config.toTriggerUser" defaultValue={false} label="resources.observation-triggers.fields.config.toTriggerUser"/>
  </>
);

const ActionConfigInput = () => (
  <FormDataConsumer>
    {({ scopedFormData }) => {

      if (scopedFormData?.type === 'alarm') {

        return (
          <>
            <SelectInput source="config.severity" choices={alarmSeverities} validate={required()} label="resources.alarms.fields.severity" shouldUnregister/>
            <SelectInput source="config.type" choices={alarmTypes} validate={required()} label="resources.alarms.fields.type" shouldUnregister/>
            <TextInput source="config.name" validate={required()} label="resources.alarms.fields.name" shouldUnregister/>
            <TextInput source="config.procedure" label="resources.alarms.fields.procedure" multiline shouldUnregister/>
          </>
        );

      }
      if (scopedFormData?.type === 'sms') {

        return (
          <>
            <ContactTargetsInput/>
            <TextInput source="config.message" validate={required()} label="general.text.message" multiline shouldUnregister/>
          </>
        );

      }
      if (scopedFormData?.type === 'email') {

        return (
          <>
            <ContactTargetsInput/>
            <TextInput source="config.subject" validate={required()} label="resources.auditlogs.text.subject" shouldUnregister/>
            <TextInput source="config.message" validate={required()} label="general.text.message" multiline shouldUnregister/>
            <SelectInput
              source="config.locale"
              choices={observationTriggerEmailLocales}
              label="resources.customers.fields.locale"
              emptyText="resources.observation-triggers.fields.config.customer_default"
              shouldUnregister
            />
          </>
        );

      }
      if (scopedFormData?.type === 'assetUpdate') {

        return (
          <>
            <SelectInput
              source="config.available"
              choices={availabilityChoices}
              label="resources.assets.fields.available"
              emptyText="resources.observation-triggers.fields.config.do_not_change"
              shouldUnregister
            />
            <SelectInput
              source="config.app.mode"
              choices={appModeOptions}
              label="general.text.app_mode"
              emptyText="resources.observation-triggers.fields.config.do_not_change"
              shouldUnregister
            />
          </>
        );

      }
      return null;

    }}
  </FormDataConsumer>
);

const ActionsInput = () => (
  <ArrayInput source="actions" validate={required()}>
    <SimpleFormIterator addButton={<AddIteratorButton translationKey="resources.observation-triggers.text.add_action"/>}>
      <SelectInput source="type" choices={actionTypes} defaultValue="alarm" validate={required()} label="resources.observation-triggers.fields.action_type"/>
      <ActionConfigInput/>
    </SimpleFormIterator>
  </ArrayInput>
);

const TargetConditionInput = ({ source = '' }: { source?: string }) => {

  const typeSource = sourcePath(source, 'type');

  return (
    <>
      <SelectInput
        source={sourcePath(source, 'match')}
        choices={matchTypes}
        defaultValue="matches"
        validate={required()}
        label="resources.observation-triggers.fields.match"
      />
      <SelectInput
        source={typeSource}
        choices={targetConditionTypes}
        defaultValue="constraint/asset"
        validate={required()}
        label="resources.observation-triggers.fields.type"
      />
      <FormDataConsumer>
        {({ formData, scopedFormData }) => {

          const conditionType = source ? get(scopedFormData, typeSource) || get(formData, typeSource) : scopedFormData?.type;
          if (conditionType === 'constraint/asset') {

            return (
              <ReferenceArrayInput source={sourcePath(source, 'assets')} reference="all-assets">
                <AutocompleteArrayInput label="menu.items.assets" validate={required()} fullWidth shouldUnregister/>
              </ReferenceArrayInput>
            );

          }
          if (conditionType === 'constraint/assetGroup') {

            return (
              <ReferenceArrayInput source={sourcePath(source, 'groups')} reference="asset-groups">
                <AutocompleteArrayInput label="resources.asset-groups.name" validate={required()} fullWidth shouldUnregister/>
              </ReferenceArrayInput>
            );

          }
          if (conditionType === 'constraint/zone') {

            return (
              <ReferenceInput source={sourcePath(source, 'zone')} reference="zones" sort={{ field: 'name', order: 'ASC' }}>
                <AutocompleteInput label="resources.zones.text.single_name" validate={required()} fullWidth shouldUnregister/>
              </ReferenceInput>
            );

          }
          return null;

        }}
      </FormDataConsumer>
    </>
  );

};

const ScheduleInput = () => (
  <ArrayInput source="filterSchedules.entries" label="resources.observation-triggers.fields.schedule_entries">
    <SimpleFormIterator disableReordering>
      <SelectArrayInput
        source="daysOfWeek"
        choices={daysOfWeek}
        validate={required()}
        label="resources.observation-triggers.fields.days"
        shouldUnregister
      />
      <TextInput source="timeStart" validate={timeValidator} label="resources.observation-triggers.fields.timeStart" shouldUnregister/>
      <TextInput source="timeEnd" validate={timeValidator} label="resources.observation-triggers.fields.timeEnd" shouldUnregister/>
    </SimpleFormIterator>
  </ArrayInput>
);

const ConditionsInput = () => {

  return (
    <>
      <FormDataConsumer>
        {({ formData }) => (formData?.filterHasUnsupportedConditions ? (
          <Typography color="warning.main" sx={{ mb: 2 }}>
            This trigger contains a filter structure that cannot be edited here. The existing filter will be preserved on save.
          </Typography>
        ) : null)}
      </FormDataConsumer>
      <SelectInput
        source="filterConditionBlockType"
        choices={conditionBlockTypes}
        defaultValue="operator/and"
        validate={required()}
        label="resources.observation-triggers.fields.condition_block"
      />
      <ArrayInput
        source="filterConditions"
        label="resources.observation-triggers.text.filter"
      >
        <SimpleFormIterator
          disableReordering
          addButton={(
            <AddIteratorButton
              translationKey="resources.observation-triggers.text.filter"
              defaultItem={defaultFilterCondition}
            />
          )}
        >
          <TargetConditionInput/>
        </SimpleFormIterator>
      </ArrayInput>
      <ScheduleInput/>
    </>
  );

};

const isSingleNegatedTargetCondition = (condition: any) => (
  condition?.type === 'operator/or'
  && condition.operands?.length === 1
  && condition.operands[0]?.type === 'operator/not'
  && targetConditionTypeIds.includes(condition.operands[0].operand?.type)
);

const hasUnsupportedObservationTriggerFilter = (filter: any): boolean => {

  if (!filter || filter.type === 'constraint/schedule' || targetConditionTypeIds.includes(filter.type)) return false;
  if (isSingleNegatedTargetCondition(filter)) return false;
  if (filter.type === 'operator/and') {

    return (filter.operands || []).some(hasUnsupportedObservationTriggerFilter);

  }
  return true;

};

const cleanConditionForApi = (condition: any) => {

  if (!condition?.type) return null;
  const {
    match,
    ...payload
  } = condition;

  if (match === 'doesNotMatch') {

    return {
      type: 'operator/or',
      operands: [{
        type: 'operator/not',
        operand: payload,
      }],
    };

  }
  return payload;

};

const negateConditionForAndOperand = (condition: any) => {

  if (isSingleNegatedTargetCondition(condition)) return condition.operands[0].operand;
  return {
    type: 'operator/or',
    operands: [{
      type: 'operator/not',
      operand: condition,
    }],
  };

};

const transformObservationTriggerForm = (data: any) => {

  const {
    filterConditions,
    filterConditionBlockType,
    filterSchedules,
    ...payload
  } = data;
  delete payload.filterHasUnsupportedConditions;

  if (hasUnsupportedObservationTriggerFilter(data.filter)) return payload;

  const conditions = (filterConditions || [])
    .map(cleanConditionForApi)
    .filter((condition: any) => condition?.type);
  const scheduleEntries = (filterSchedules?.entries || [])
    .filter((entry: any) => entry?.daysOfWeek?.length && entry?.timeStart && entry?.timeEnd);
  const conditionOperands = filterConditionBlockType === 'operator/not' && conditions.length
    ? conditions.map(negateConditionForAndOperand)
    : conditions;
  const operands = [
    ...conditionOperands,
    ...(scheduleEntries.length ? [{
      type: 'constraint/schedule',
      schedule: {
        entries: scheduleEntries,
      },
    }] : []),
  ];

  return {
    ...payload,
    filter: {
      type: 'operator/and',
      operands,
    },
  };

};

const ObservationTriggerForm = ({ toolbar }: { toolbar?: React.ReactElement }) => {

  const translate = useTranslate();
  return (
    <SimpleForm toolbar={toolbar}>
      <Typography variant="h6" gutterBottom>
        {translate('resources.observation-triggers.text.general')}
      </Typography>
      <TextInput source="name" validate={required()} label="general.fields.name"/>
      <EventCodesInput/>
      <TextInput source="description" multiline/>
      <BooleanInput source="status" defaultValue="active" format={(v) => (v === 'active')} parse={(v) => (v ? 'active' : 'inactive')}
        label="resources.observation-triggers.fields.active_status"/>
      <Typography variant="h6" gutterBottom>
        {translate('resources.observation-triggers.text.action_config')}
      </Typography>
      <ActionsInput/>
      <Typography variant="h6" gutterBottom>
        {translate('resources.observation-triggers.text.filter')}
      </Typography>
      <ConditionsInput/>
    </SimpleForm>
  );

};

export const ObservationTriggerList = () => (
  <>
    <StabilityAlert stability={StabilityLevel.Stable}/>
    <List filters={SearchFields} debounce={LIST_DEBOUNCE} exporter={exporter} title="resources.observation-triggers.text.title">
      <>
        <FilterResetter/>
        <Datagrid rowClick="toggleSelection" bulkActionButtons={<CustomBulkActionButtons/>}>
          <TextField source="name" label="general.fields.name"/>
          <SimpleChipListField source="eventCodes"/>
          <TextField source="description"/>
          <ActiveBooleanField source="status" />
          <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
          <DateFieldWithTime source="updatedAt" label="general.fields.updatedAt" timeOnHover={true}/>
          <IfCanAccess action="edit">
            <EditButton/>
          </IfCanAccess>
          <HasRoles anyOf={['developer_admin']}>
            <AuditlogButton/>
          </HasRoles>
        </Datagrid>
      </>
    </List>
  </>
);

export const ObservationTriggerEdit = () => {

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable}/>
      <EditWithSuccess title="resources.observation-triggers.text.title" transform={transformObservationTriggerForm}>
        <ObservationTriggerForm toolbar={<CustomToolbar/>}/>
      </EditWithSuccess>
    </>
  );

};

export const ObservationTriggerCreate = () => {

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable}/>
      <CreateWithSuccess title="resources.observation-triggers.text.title" transform={transformObservationTriggerForm}>
        <ObservationTriggerForm/>
      </CreateWithSuccess>
    </>
  );

};
